import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import useCampaignSpellSync from '../hooks/useCampaignSpellSync';
import { ABILITY_NAMES, MAX_ROLL_REQUEST_TARGETS, ROLL_REQUEST_ABILITIES, SKILLS, fateHelpNote, groupCheckLine, groupCheckOutcome, rollRequestLabel,
  rollRequestLogLine } from '../../../shared/rules/rollRequests.mjs';
import './RollRequests.scss';

const newId = () => window.crypto?.randomUUID?.() || `roll-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const EMPTY = { kind: 'check', skill: 'athletics', tool: '', ability: 'str', dc: 15, forcedMode: '', reason: '', hiddenDc: false, group: false, charmOrFear: false };

/** One character's line: waiting, or the roll against the DC with any rescue or another character's Improve Fate. */
export const rollRequestOutcome = (target, dc) => {
  const result = target.result;
  if (!result) return 'Waiting for the roll';
  const rescue = result.strokeOfLuck ? ' · Stroke of Luck' : result.indomitable ? ' · Indomitable' : result.heroicInspiration ? ' · Heroic Inspiration'
    : result.fate ? ` · Boon of Fate +${result.fate.bonus}` : result.bardicInspiration ? ` · Bardic Inspiration +${result.bardicInspiration.bonus}` : result.peerlessSkill ? ` · Peerless Skill +${result.peerlessSkill.bonus}` : result.countercharm ? ` · Countercharm (${result.countercharm.by})` : '';
  return `${result.automaticFailure ? 'Automatic failure' : `${result.total} vs DC ${dc}`}${rescue}${fateHelpNote(result)} · ${result.success ? 'Success' : 'Failure'}`;
};

/**
 * The DM asks characters for an ability check or saving throw with a DC; each player rolls it on their sheet. Open
 * requests and their results update live; the DM closes a request when done. `renderTrigger(open)` draws the opener
 * (the DM Tools menu item).
 */
export default function RollRequestControls({ campaignId, renderTrigger, onResult }) {
  const [open, setOpen] = useState(false), [roster, setRoster] = useState(null), [selected, setSelected] = useState([]);
  const [form, setForm] = useState(EMPTY), [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const pending = useRef(null), seen = useRef(null), logResult = useRef(onResult);
  logResult.current = onResult;
  const base = `/campaigns/${encodeURIComponent(campaignId || '')}/roll-requests`;
  const load = useCallback(async () => {
    const response = await apiFetch(`${base}/sync`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
    if (!response.ok) throw new Error('Roll requests could not be loaded.');
    return (await response.json()).requests || [];
  }, [base]);
  // New results reach the DM as a note even while the window is closed.
  const apply = useCallback(next => {
    const results = new Map(next.flatMap(request => request.targets.filter(target => target.result)
      .map(target => [`${request.requestId}:${target.characterId}:${target.result.total}:${target.result.success}`, { request, target }])
      .concat(request.spec.group && groupCheckOutcome(request.targets).success !== null
        ? [[`${request.requestId}:group`, { request, group: groupCheckOutcome(request.targets) }]] : [])));
    if (seen.current) {
      for (const [id, { request, target, group }] of results) {
        if (seen.current.has(id)) continue;
        // A group check's outcome, once everyone has rolled.
        if (group) {
          notify(`${rollRequestLabel(request.spec)} · ${groupCheckLine(group)}`, group.success ? 'success' : 'info');
          logResult.current?.({ type: 'roll', source: `Group · ${rollRequestLabel(request.spec)}`, breakdown: groupCheckLine(group) });
          continue;
        }
        notify(`${target.name}: ${rollRequestLabel(request.spec)} · ${rollRequestOutcome(target, request.spec.dc)}`, target.result.success ? 'success' : 'info');
        // New results also go to the DM's roll log (ones already there when the page loaded do not repeat).
        logResult.current?.({ type: 'roll', source: `${target.name} · ${rollRequestLabel(request.spec)}`, total: target.result.total,
          breakdown: rollRequestLogLine(request.spec, target, target.result) });
      }
    }
    seen.current = new Set(results.keys());
    setRequests(previous => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next));
  }, []);
  useCampaignSpellSync({ campaignId, kind: 'rollRequests', load, onData: apply });
  useEffect(() => { setOpen(false); setRequests([]); seen.current = null; pending.current = null; }, [campaignId]);
  useEffect(() => {
    if (!open || roster) return undefined;
    let live = true;
    (async () => {
      try {
        const response = await apiFetch(`/campaigns/${encodeURIComponent(campaignId)}/rests`, { cache: 'no-store' });
        const data = await response.json(); if (!response.ok) throw new Error(data.message || 'Could not load characters.');
        if (live) setRoster(data.characters || []);
      } catch (failure) { if (live) setError(failure.message); }
    })();
    return () => { live = false; };
  }, [open, roster, campaignId]);
  const change = (field, value) => { pending.current = null; setForm(previous => ({ ...previous, [field]: value,
    ...(field === 'skill' && value ? { ability: SKILLS[value].ability, tool: '' } : {}) })); };
  const toggle = id => { pending.current = null; setSelected(previous => (previous.includes(id) ? previous.filter(other => other !== id) : [...previous, id])); };
  const send = async () => {
    if (busy || !selected.length) return;
    setBusy(true); setError('');
    // The same request id while retrying an unchanged form, so a retry cannot ask twice.
    const body = pending.current || { id: newId(), kind: form.kind, ability: form.ability, dc: Number(form.dc), characterIds: selected,
      ...(form.kind === 'check' && form.skill ? { skill: form.skill } : {}), ...(form.kind === 'check' && !form.skill && form.tool.trim() ? { tool: form.tool.trim() } : {}),
      ...(form.forcedMode ? { forcedMode: form.forcedMode } : {}), ...(form.reason.trim() ? { reason: form.reason.trim() } : {}), hiddenDc: form.hiddenDc, ...(save && form.charmOrFear ? { charmOrFear: true } : {}),
      ...(form.kind === 'check' && form.group ? { group: true } : {}) };
    pending.current = body;
    try {
      const response = await apiFetch(base, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) { if (response.status < 500) pending.current = null; throw new Error(result.message || 'The roll could not be requested.'); }
      pending.current = null; setSelected([]);
      notify(`Requested: ${rollRequestLabel(result.request.spec)}, DC ${result.request.spec.dc}.`, 'success');
      apply(await load());
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  const close = async requestId => {
    try {
      const response = await apiFetch(`${base}/${encodeURIComponent(requestId)}/close`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).message || 'The request could not be closed.');
      apply(await load());
    } catch (failure) { setError(failure.message); }
  };
  const save = form.kind === 'save';
  // One picker for the roll: a plain check, a skill check or a saving throw.
  const rollValue = save ? 'save' : `check:${form.skill}`;
  const pickRoll = value => {
    pending.current = null;
    if (value === 'save') return setForm(previous => ({ ...previous, kind: 'save' }));
    const skill = value.slice('check:'.length);
    return setForm(previous => ({ ...previous, kind: 'check', skill, tool: skill ? '' : previous.tool, ...(skill ? { ability: SKILLS[skill].ability } : {}) }));
  };
  const allSelected = Boolean(roster?.length) && roster.slice(0, MAX_ROLL_REQUEST_TARGETS).every(character => selected.includes(character.id));
  const waiting = requests.reduce((count, request) => count + request.targets.filter(target => !target.result).length, 0);
  return <>
    {renderTrigger ? renderTrigger(() => setOpen(true)) : <Button variant="outline-light" size="sm" onClick={() => setOpen(true)}>Request a roll</Button>}
    <Modal scrollable show={open} onHide={() => !busy && setOpen(false)} centered className="dnd-modal modern-modal roll-request-form">
      <Modal.Header closeButton={!busy}><Modal.Title as="h6" className="mb-0">Request a roll</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-1 small">{error}</Alert>}
        <div className="roll-request__grid">
          <Form.Select size="sm" aria-label="Roll" value={rollValue} onChange={event => pickRoll(event.target.value)}>
            <option value="check:">Check (no skill)</option>
            {Object.entries(SKILLS).map(([skill, { name }]) => <option key={skill} value={`check:${skill}`}>Check · {name}</option>)}
            <option value="save">Saving throw</option>
          </Form.Select>
          <Form.Select size="sm" aria-label="Ability" value={form.ability} onChange={event => change('ability', event.target.value)}>
            {ROLL_REQUEST_ABILITIES.map(ability => <option key={ability} value={ability}>{ABILITY_NAMES[ability]}</option>)}
          </Form.Select>
          <Form.Control size="sm" aria-label="DC" type="number" min={1} max={40} value={form.dc} onChange={event => change('dc', event.target.value)} />
          <Form.Select size="sm" aria-label="Advantage" value={form.forcedMode} onChange={event => change('forcedMode', event.target.value)}>
            <option value="">From the sheet</option><option value="advantage">Grant Advantage</option><option value="disadvantage">Impose Disadvantage</option>
          </Form.Select>
          {!save && !form.skill && <Form.Control size="sm" aria-label="Tool (optional)" value={form.tool} maxLength={60} placeholder="Tool (optional)"
            onChange={event => change('tool', event.target.value)} style={{ gridColumn: '1 / -1' }} />}
        </div>
        <div className="roll-request__row mb-2">
          <Form.Control size="sm" aria-label="Reason (optional)" value={form.reason} maxLength={140} placeholder="Reason (optional)" onChange={event => change('reason', event.target.value)} />
          <Form.Check id="roll-request-hidden" className="text-nowrap small mb-0" label="Hide DC" checked={form.hiddenDc} onChange={event => change('hiddenDc', event.target.checked)} />
          {save && <Form.Check id="roll-request-charm" className="text-nowrap small mb-0" label="Charmed or Frightened" title="A failed save applies Charmed or Frightened, so a Bard's Countercharm can answer it." checked={form.charmOrFear} onChange={event => change('charmOrFear', event.target.checked)} />}
          {!save && <Form.Check id="roll-request-group" className="text-nowrap small mb-0" label="Group check" checked={form.group} onChange={event => change('group', event.target.checked)} />}
        </div>
        <div className="roll-request__muted mb-1">Characters{roster?.length ? <> · <button type="button" className="roll-request__link"
          onClick={() => { pending.current = null; setSelected(allSelected ? [] : roster.slice(0, MAX_ROLL_REQUEST_TARGETS).map(character => character.id)); }}>
          {allSelected ? 'Clear' : 'Select all'}</button></> : null}</div>
        {!roster && !error && <Spinner role="status" animation="border" size="sm"><span className="visually-hidden">Loading characters</span></Spinner>}
        {roster && !roster.length && <div className="roll-request__muted">No characters in this campaign.</div>}
        {/* Every character as a chip; they wrap onto as many rows as they need. */}
        <div className="roll-request__chips">
          {roster?.map(character => <button key={character.id} type="button" className="roll-request__chip" aria-pressed={selected.includes(character.id)}
            disabled={!selected.includes(character.id) && selected.length >= MAX_ROLL_REQUEST_TARGETS} onClick={() => toggle(character.id)}>{character.name}</button>)}
        </div>
        <div className="roll-request__row roll-request__footer">
          <span className="roll-request__muted">{requests.length ? `${requests.length} open request${requests.length === 1 ? '' : 's'}${waiting ? ` · ${waiting} waiting` : ''}` : rollRequestLabel(form)}</span>
          <Button size="sm" disabled={busy || !selected.length} onClick={send}>{busy ? 'Sending…' : `Ask ${selected.length || ''}`.trim()}</Button>
        </div>
        {requests.map(request => <div key={request.requestId} className="mt-2">
          <div className="roll-request__row">
            <span className="small fw-semibold">{rollRequestLabel(request.spec)} · DC {request.spec.dc}{request.spec.hiddenDc ? ' (hidden)' : ''}</span>
            <Button size="sm" variant="outline-light" className="py-0" onClick={() => close(request.requestId)}>Close</Button>
          </div>
          {request.spec.reason && <div className="roll-request__muted">{request.spec.reason}</div>}
          {request.targets.map(target => <div key={target.characterId} className="roll-request__muted">{target.name}: {rollRequestOutcome(target, request.spec.dc)}</div>)}
          {request.spec.group && <div className="small">{groupCheckLine(groupCheckOutcome(request.targets))}</div>}
        </div>)}
      </Modal.Body>
    </Modal>
  </>;
}
